<script setup lang="ts">
import {
  BENEFIT_GROUPS,
  hexAlpha,
  levelBenefits,
  matrixColumns,
} from '@/utils/levels';

const props = defineProps<{
  /** Índice do elo do jogador em `LEVEL_TIERS`. */
  playerTierIndex: number;
}>();

/**
 * Uma cor por grupo de `BENEFIT_GROUPS`, na mesma ordem: loja em azul, retorno
 * em dinheiro em verde, atendimento VIP em amarelo. Serve só pro cabeçalho do
 * grupo — separa os três blocos sem precisar de moldura.
 */
const GROUP_COLORS = ['#8FD0FF', '#4FE0A0', '#FFD84A'] as const;

/** Raio das pontas da faixa zebrada de cada linha. */
const BAND_RADIUS = 8;

/** Recuo da faixa inteira em relação ao título da seção. */
const BAND_INDENT = 12;

/**
 * A coluna do jogador. As 7 faixas VIP viram uma coluna só, então qualquer
 * índice de elo VIP cai na última coluna.
 */
const allColumns = computed(() => matrixColumns());

const currentColumn = computed(() =>
  Math.min(props.playerTierIndex, allColumns.value.length - 1),
);

const columns = computed(() =>
  allColumns.value.map((column, index) => {
    const isCurrent = index === currentColumn.value;
    const isPast = index < currentColumn.value;

    return {
      ...column,
      isCurrent,
      // Sombra colorida caindo do brasão, como no protótipo — não é halo: o
      // deslocamento de 4px pra baixo é o que dá o assentado da arte.
      // Bloqueado troca a luz por dessaturação.
      badgeFilter:
        isCurrent || isPast
          ? `drop-shadow(0 4px 10px ${hexAlpha(column.color, 0.5)}) drop-shadow(0 0 6px ${hexAlpha(column.color, 0.42)})`
          : 'saturate(.55) brightness(.78)',
      // Mancha de cor atrás do brasão. Mesma receita dos brilhos do protótipo:
      // círculo na cor cheia, `screen` e opacidade baixa — o alpha na cor
      // deixaria a mancha suja em vez de acender o fundo.
      badgeGlow: column.color,
      badgeGlowOpacity: isCurrent ? 0.78 : isPast ? 0.6 : 0.2,
      // O elo atual ganha 6px de brasão. As linhas do cabeçalho (nome e faixa)
      // continuam alinhadas porque a célula é `align-bottom`: quem cresce é a
      // pilha, de baixo pra cima.
      badgeSize: isCurrent ? '46px' : '34px',
      headStyle: isCurrent
        ? {
            background: hexAlpha(column.color, 0.12),
            borderRadius: '10px 10px 0 0',
          }
        : {},
    };
  }),
);

/**
 * Arrastar com o mouse. O `overflow-x` nativo só entrega barra de rolagem e
 * trackpad — no desktop, clicar e empurrar a tabela precisa de JS.
 */
const scroller = ref<HTMLElement | null>(null);
const isDragging = ref(false);

/** Posição do ponteiro e do scroll no instante do clique. */
let dragStartX = 0;
let dragStartScroll = 0;

const onPointerDown = (event: PointerEvent) => {
  // Só botão principal: com o do meio o browser tem o próprio auto-scroll e
  // com o direito abriria o menu de contexto no meio do arraste.
  if (event.button !== 0 || !scroller.value) return;

  // No toque o browser já faz o pan nativo; mexer no scrollLeft por cima
  // dobraria a velocidade do arraste.
  if (event.pointerType === 'touch') return;

  isDragging.value = true;
  dragStartX = event.clientX;
  dragStartScroll = scroller.value.scrollLeft;

  // Captura no elemento: o arraste continua mesmo saindo da tabela, sem
  // precisar de listener no document.
  scroller.value.setPointerCapture(event.pointerId);
};

const onPointerMove = (event: PointerEvent) => {
  if (!isDragging.value || !scroller.value) return;

  // O conteúdo segue o dedo: arrastar pra esquerda avança o scroll.
  scroller.value.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
};

const endDrag = (event: PointerEvent) => {
  if (!isDragging.value) return;

  isDragging.value = false;
  scroller.value?.releasePointerCapture(event.pointerId);
};

/**
 * Uma linha por benefício, na ordem agrupada de `LEVEL_BENEFITS`; abre um
 * cabeçalho de grupo a cada troca de `group`.
 */
const rows = computed(() => {
  const benefits = levelBenefits();

  return benefits.map((benefit, index) => {
    const previousGroup = index === 0 ? null : benefits[index - 1].group;
    // A faixa zebrada é desenhada célula a célula (não no `<tr>`), senão não dá
    // pra arredondar as pontas nem afastar o rótulo da borda.
    const band = index % 2 === 1 ? 'rgba(255,255,255,.02)' : 'transparent';
    const lastColumn = allColumns.value.length - 1;

    return {
      label: benefit.label,
      groupStart: benefit.group !== previousGroup,
      groupLabel: BENEFIT_GROUPS[benefit.group],
      groupColor: GROUP_COLORS[benefit.group] ?? '#FFFFFF',
      labelStyle: {
        background: band,
        borderRadius: `${BAND_RADIUS}px 0 0 ${BAND_RADIUS}px`,
        // A borda transparente + `padding-box` empurram a faixa inteira pra
        // direita, não só o texto: numa célula de tabela é o único jeito de
        // recuar o fundo sem mexer no alinhamento das colunas de check.
        borderLeft: `${BAND_INDENT}px solid transparent`,
        backgroundClip: 'padding-box',
        // Essa mesma borda é um buraco na coluna travada: a camada opaca
        // (::after) se mede pela caixa de PADDING, então precisa saber o
        // tamanho do recuo pra esticar por cima da borda também.
        '--band-indent': `${BAND_INDENT}px`,
      },
      cells: allColumns.value.map((column, columnIndex) => {
        // "Novo" = o benefício abre dentro desta coluna. "Herdado" = já vinha
        // de uma coluna anterior. Assim a matriz mostra onde cada coisa entra.
        const isNew =
          !benefit.pending &&
          benefit.from >= column.from &&
          benefit.from <= column.to;

        const isInherited =
          !benefit.pending && !isNew && benefit.from < column.from;

        return {
          key: column.name,
          isNew,
          isInherited,
          isAbsent: !isNew && !isInherited,
          style: {
            // Só a faixa da linha: o realce do elo atual vive no cabeçalho, e
            // repetir o tint em cada célula competia com a zebra.
            background: band,
            borderRadius:
              columnIndex === lastColumn
                ? `0 ${BAND_RADIUS}px ${BAND_RADIUS}px 0`
                : '0',
          },
        };
      }),
    };
  });
});
</script>

<template>
  <section class="flex flex-col gap-4">
    <div class="flex flex-col gap-1.5">
      <h2
        class="text-[22px] font-extrabold leading-[27px] tracking-[-0.04em] text-white"
      >
        Comparativo de benefícios
      </h2>
      <p class="max-w-[560px] text-[13px] leading-[18px] text-white/[0.62]">
        O que cada elo libera, lado a lado. As sete faixas VIP entram numa
        coluna só.
      </p>
    </div>

    <!-- Um scroll horizontal só: a coluna de rótulos fica travada com
         `position: sticky` e as colunas de elo passam por baixo dela junto com
         o próprio cabeçalho (thead e tbody são a MESMA tabela, então brasão e
         célula nunca saem de registro). O snap por coluna deixa o arraste
         parando em cima de um elo, não no meio de dois. -->
    <div class="levels-matrix-frame">
      <!-- Esfumaçado na borda direita, fora do scroller: avisa que a tabela
           continua. Fica sobre o conteúdo, mas sem capturar o arraste. -->
      <div
        aria-hidden="true"
        class="levels-matrix-fade"
      ></div>

      <div
        ref="scroller"
        class="levels-matrix"
        :class="isDragging ? 'levels-matrix--dragging' : ''"
        @pointercancel="endDrag"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="endDrag"
      >
        <!-- `w-max` em vez de `w-full`: a tabela tem que ficar MAIOR que o
           container (240 de rótulo + 8 × 128 = 1264px) pra sobrar um par de
           colunas fora da tela. Com `w-full` as 8 colunas se comprimiam pra
           caber e não havia o que arrastar. -->
        <table class="w-max border-separate [border-spacing:0] text-left">
          <thead>
            <tr>
              <th class="levels-matrix__label w-[240px] p-0"></th>
              <th
                v-for="column in columns"
                :key="column.name"
                class="levels-matrix__col w-[128px] rounded-xl px-3 pb-3 pt-7 align-bottom"
                :style="column.headStyle"
              >
                <div class="flex flex-col items-center gap-1.5">
                  <span
                    class="levels-col-badge"
                    :style="{
                      '--glow': column.badgeGlow,
                      '--glow-opacity': column.badgeGlowOpacity,
                      '--badge-size': column.badgeSize,
                    }"
                  >
                    <!-- A flutuação vive na ARTE, não no invólucro: a mancha de
                         luz é um ::before do invólucro e ficaria pingando junto
                         com o brasão. -->
                    <span
                      class="levels-col-badge__art"
                      :class="column.isCurrent ? 'levels-col-float' : ''"
                      :style="{
                        backgroundImage: `url(${column.badge})`,
                        filter: column.badgeFilter,
                      }"
                    ></span>
                  </span>
                  <span
                    class="whitespace-nowrap text-[12.5px] font-bold leading-[17px] tracking-[-0.01em]"
                    :style="{
                      color: column.isCurrent ? '#fff' : 'rgba(255,255,255,.8)',
                    }"
                  >
                    {{ column.name }}
                  </span>
                  <span
                    class="whitespace-nowrap text-[10px] font-medium leading-[14px] text-white/[0.5] tabular-nums"
                  >
                    {{ column.range }}
                  </span>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <template
              v-for="row in rows"
              :key="row.label"
            >
              <!-- Cabeçalho de grupo -->
              <tr v-if="row.groupStart">
                <td
                  class="pb-1.5 pt-4"
                  :colspan="columns.length + 1"
                >
                  <span class="flex items-center gap-3">
                    <!-- Só o rótulo é sticky (a régua tem que continuar
                       esticando): o `pr-3` + fundo opaco impedem que as
                       colunas passem por baixo do texto. -->
                    <span
                      class="levels-matrix__group whitespace-nowrap px-3 text-[12px] font-bold uppercase leading-[16px] tracking-[0.16em]"
                      :style="{ color: row.groupColor }"
                    >
                      {{ row.groupLabel }}
                    </span>
                    <!-- Régua branca, não na cor do grupo: a cor fica só no
                       rótulo, senão os três blocos brigam entre si. -->
                    <span
                      class="h-px flex-1 bg-[linear-gradient(90deg,rgba(255,255,255,.14),transparent)]"
                    ></span>
                  </span>
                </td>
              </tr>

              <tr>
                <th
                  class="levels-matrix__label py-3 pl-3 pr-2.5 text-[14px] font-medium leading-[19px] text-white"
                  scope="row"
                  :style="row.labelStyle"
                >
                  {{ row.label }}
                </th>
                <td
                  v-for="cell in row.cells"
                  :key="cell.key"
                  class="py-3 text-center align-middle"
                  :style="cell.style"
                >
                  <!-- Verde = abre aqui; cinza = já vinha de antes; travessão = não tem -->
                  <span
                    v-if="cell.isNew"
                    class="mx-auto flex h-[22px] w-[22px] items-center justify-center rounded-full"
                    :style="{
                      border: '1.5px solid rgba(79,224,160,.9)',
                      background: 'rgba(79,224,160,.18)',
                      boxShadow: '0 0 14px rgba(79,224,160,.35)',
                    }"
                  >
                    <svg
                      class="block h-[12px] w-[12px]"
                      fill="none"
                      stroke="#5BF0AE"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3.2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M5 12.5l4.5 4.5L19 7.5" />
                    </svg>
                  </span>
                  <svg
                    v-else-if="cell.isInherited"
                    class="mx-auto block h-[13px] w-[13px]"
                    fill="none"
                    stroke="rgba(255,255,255,.62)"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="3"
                    viewBox="0 0 24 24"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                  <span
                    v-else
                    class="mx-auto block h-px w-3 bg-white/[0.18]"
                  ></span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<style scoped lang="postcss">
/* Nesta altura da página o fundo do gradiente do elo já virou chapado #020716
   (o radial morre em 46%), então é essa a cor que a coluna travada pinta. */
.levels-matrix-frame {
  --matrix-bg: #020716;

  position: relative;
}

.levels-matrix-fade {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  width: 56px;
  background: linear-gradient(90deg, transparent, var(--matrix-bg));
  pointer-events: none;
}

.levels-matrix {
  /* A mãozinha é o convite: sem ela ninguém descobre que dá pra arrastar. */
  cursor: grab;
  overflow-x: auto;
  /* O arraste não vaza pro scroll da página/histórico do navegador. */
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  /* Sem isso o snap encaixa a coluna EMBAIXO da coluna travada. */
  scroll-padding-left: 240px;
}

/* Durante o arraste: nada de seleção de texto (o cursor varre os rótulos) e
   nada de snap — o encaixe brigaria com o scrollLeft que o ponteiro escreve.
   Ao soltar, o snap volta e a coluna assenta sozinha. */
.levels-matrix--dragging {
  cursor: grabbing;
  scroll-snap-type: none;
  user-select: none;
}

.levels-matrix__col {
  scroll-snap-align: start;
}

.levels-matrix__label {
  position: sticky;
  left: 0;
  z-index: 2;
  /* Sombra curta só pra direita: dá o degrau que faz a coluna ler como
     travada quando as outras passam por baixo. */
  box-shadow: 12px 0 14px -12px rgb(0 0 0 / 85%);
}

/* O fundo da célula é a faixa zebrada (translúcida, inline) e a borda esquerda
   transparente é o recuo dela — as duas coisas deixariam as colunas aparecendo
   por baixo. O ::after entra como camada opaca ATRÁS do fundo da própria
   célula: o `z-index` da célula já abre contexto de empilhamento, então o -1
   não escapa pro fundo da página. */
.levels-matrix__label::after {
  content: '';
  position: absolute;
  inset: 0;
  /* Estica pra dentro da borda transparente do recuo (`--band-indent`), senão
     sobra uma tira de ~12px por onde as colunas aparecem. No cabeçalho não há
     recuo, daí o fallback 0. */
  left: calc(-1 * var(--band-indent, 0px));
  z-index: -1;
  background: var(--matrix-bg);
}

.levels-matrix__group {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--matrix-bg);
}

@keyframes levelsColFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.levels-col-float {
  animation: levelsColFloat 3.6s ease-in-out infinite;
}

.levels-col-badge {
  position: relative;
  display: block;
  width: var(--badge-size, 34px);
  height: var(--badge-size, 34px);
}

/* Simétrica e com o fade terminando ANTES da borda da caixa (transparente em
   72% de 9999px de raio): assim a mancha morre sozinha, redonda. Assimetria ou
   fade colado na borda deixa o topo cortado reto. O raio de 38% ≈ 13px, mais os
   4px que a flutuação sobe: o `pt-7` (28px) do cabeçalho é o que garante que a
   mancha morra ANTES da borda do realce do elo atual — com o `p-3` de antes o
   `screen` trocava de fundo na quina do tint e a luz saía cortada reta. */
.levels-col-badge::before {
  content: '';
  position: absolute;
  inset: -38%;
  border-radius: 9999px;
  background: radial-gradient(
    circle,
    var(--glow) 0%,
    color-mix(in srgb, var(--glow) 68%, transparent) 30%,
    transparent 72%
  );
  mix-blend-mode: screen;
  opacity: var(--glow-opacity);
  pointer-events: none;
}

.levels-col-badge__art {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
